/**
 * Blocking modal: shown when the logged-in user hasn't accepted the
 * current ToS version for their tenant.
 *
 * Mounted in AppLayout so it sits above all routed content. Reads the
 * brand client's ToS row (already loaded by AppLayout) and the current
 * user's `tos_accepted_version`.
 */
import { TosMarkdown } from "@/components/TosMarkdown";
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";

export default function ToSAcceptanceGate({ user, brandClient }) {
  const [tos, setTos] = useState(null);          // { markdown, version }
  const [accepted, setAccepted] = useState(true); // assume OK until we know
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id || !brandClient?.id) return undefined;
    let cancelled = false;
    (async () => {
      const [{ data: client }, { data: profile }] = await Promise.all([
        supabase
          .from("lbs_clients")
          .select("tos_markdown, tos_version")
          .eq("id", brandClient.id)
          .maybeSingle(),
        supabase
          .from("lbs_user_profiles")
          .select("tos_accepted_version")
          .eq("id", user.id)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      const required = client?.tos_version || 0;
      const has      = profile?.tos_accepted_version || 0;
      const text     = (client?.tos_markdown || "").trim();
      if (!text || required === 0) {
        setAccepted(true);
        return;
      }
      setTos({ markdown: text, version: required });
      setAccepted(has >= required);
    })();
    return () => { cancelled = true; };
  }, [user?.id, brandClient?.id]);

  const accept = async () => {
    if (!tos) return;
    setBusy(true);
    // Use the SECURITY DEFINER RPC (063) so RLS column-update policies
    // can't silently swallow the write.
    const { error } = await supabase.rpc("lbs_accept_tos", { p_version: tos.version });
    setBusy(false);
    if (error) {
      toast.error(error.message || "Could not record acceptance");
      return;
    }
    setAccepted(true);
  };

  if (accepted || !tos) return null;

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl"
        data-testid="tos-acceptance-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Terms of Service (v{tos.version})</DialogTitle>
          <DialogDescription>
            Please review and accept the Terms of Service to continue using the app.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-3">
          <TosMarkdown text={tos.markdown} className="text-sm" testid="tos-gate-text" />
        </ScrollArea>
        <DialogFooter>
          <Button onClick={accept} disabled={busy} data-testid="tos-accept-btn">
            {busy ? "Recording…" : "I accept these terms"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
